import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Target, Plus, Pencil, Trash2, Zap, ChevronRight } from 'lucide-react';
import BBALoader from '../components/BBALoader';
import BackButton from '../components/BackButton';
import Modal from '../components/Modal';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useDepartmentOwnerId } from '../hooks/useDepartmentOwnerId';
import { useSalesDashboard } from '../hooks/useSalesDashboard';
import { useMarketingDashboard } from '../hooks/useMarketingDashboard';
import { useFinanceAnalytics } from '../hooks/useFinanceAnalytics';
import {
  getUserGoals,
  updateUserGoal,
  addUserGoal,
  deleteUserGoal
} from '../services/firebase/userGoalsService';
import PortalBackground from '../components/PortalBackground';

const DEPARTMENTS = ['Sales', 'Operations', 'Technology', 'Finance', 'Marketing'];

// Sidebar emoji + brand color per department. `light`/`lightText` style the
// resting tab; `active` styles the selected tab (dark filled).
const DEPARTMENT_STYLES = {
  Sales: {
    emoji: '🚀',
    light: 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/40',
    active: 'bg-red-600 text-white shadow-sm'
  },
  Operations: {
    emoji: '⚙️',
    light: 'bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/40',
    active: 'bg-blue-600 text-white shadow-sm'
  },
  Technology: {
    emoji: '🤖',
    light: 'bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-900/20 dark:text-purple-300 dark:hover:bg-purple-900/40',
    active: 'bg-purple-600 text-white shadow-sm'
  },
  Finance: {
    emoji: '💵',
    light: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/40',
    active: 'bg-emerald-600 text-white shadow-sm'
  },
  Marketing: {
    emoji: '📣',
    light: 'bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-300 dark:hover:bg-orange-900/40',
    active: 'bg-orange-600 text-white shadow-sm'
  }
};

const UNIT_OPTIONS = [
  { value: 'usd', label: 'USD ($)' },
  { value: 'percent', label: 'Percentage (%)' },
  { value: 'number', label: 'Number' },
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' }
];

// Metrics a goal can sync its "current" value from. Values resolve live from
// the JobTread-backed dashboard hooks; `unit` is forced onto linked goals.
const LINKED_METRICS = {
  revenue: { label: 'Revenue', window: 'This year', unit: 'usd', group: 'Sales' },
  projectsSold: { label: 'Projects Sold', window: 'This year', unit: 'number', group: 'Sales' },
  avgJobSize: { label: 'Average Job Size', window: 'This year', unit: 'usd', group: 'Sales' },
  closeRatio: { label: 'Close Ratio', window: 'This year', unit: 'percent', group: 'Sales' },
  leads_month: { label: 'Leads', window: 'This month', unit: 'number', group: 'Marketing' },
  leads_quarter: { label: 'Leads', window: 'This quarter', unit: 'number', group: 'Marketing' },
  leads_year: { label: 'Leads', window: 'This year', unit: 'number', group: 'Marketing' },
  gpm: { label: 'Gross Profit Margin', window: 'This year', unit: 'percent', group: 'Finance' },
  grossProfit: { label: 'Gross Profit', window: 'This year', unit: 'usd', group: 'Finance' },
  netMargin: { label: 'Net Margin', window: 'This year', unit: 'percent', group: 'Finance' },
  netProfit: { label: 'Net Profit', window: 'This year', unit: 'usd', group: 'Finance' }
};

const METRIC_GROUPS = ['Sales', 'Marketing', 'Finance'];

const formatValue = (value, unit) => {
  const n = Number(value) || 0;
  switch (unit) {
    case 'usd':
      return `$${Math.round(n).toLocaleString()}`;
    case 'percent':
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    case 'seconds':
      return `${n}s`;
    case 'days':
      return `${n.toLocaleString()} days`;
    case 'months':
      return `${n.toLocaleString()} months`;
    default:
      return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
};

const roundForUnit = (value, unit) =>
  unit === 'percent' ? Math.round(value * 10) / 10 : Math.round(value);

const calculateProgress = (actual, target) => {
  if (!target || target <= 0) return 0;
  return Math.min(100, Math.round((actual / target) * 100));
};

const progressTone = (progress) => {
  if (progress >= 100) {
    return {
      bar: 'bg-gradient-to-r from-emerald-400 to-emerald-600',
      text: 'text-emerald-600 dark:text-emerald-400',
      chip: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
    };
  }
  if (progress >= 60) {
    return {
      bar: 'bg-gradient-to-r from-amber-400 to-amber-500',
      text: 'text-amber-600 dark:text-amber-400',
      chip: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
    };
  }
  return {
    bar: 'bg-gradient-to-r from-red-500 to-rose-500',
    text: 'text-red-600 dark:text-red-400',
    chip: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
  };
};

const inputClass =
  'w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-400';

const labelClass = 'block text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5';

const SyncedBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
    <Zap className="w-3 h-3" />
    JobTread
  </span>
);

// Compact unit labels for the picker embedded in the big target input
const UNIT_SHORT = { usd: '$', percent: '%', number: '#', days: 'days', months: 'mo' };

const EMPTY_FORM = {
  metricName: '',
  department: 'Sales',
  unit: 'usd',
  target: '',
  actual: '',
  linkedMetric: ''
};

const Goals = () => {
  const { isOnboardingMode, markStepComplete } = useOnboarding();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('Sales');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [goals, setGoals] = useState({});
  const { ownerId: goalsOwnerId } = useDepartmentOwnerId();

  // Live metrics for JobTread-linked goals
  const salesData = useSalesDashboard('ytd', { jobtreadOnly: true });
  const marketingData = useMarketingDashboard('ytd');
  const financeData = useFinanceAnalytics('ytd');
  const liveLoading = salesData.loading || marketingData.loading || financeData.loading;
  const jobtreadConnected = !!salesData.jobtreadConnected;

  // Single modal for add + edit
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null); // null = adding
  const [editingDept, setEditingDept] = useState(null); // department the goal lives in
  const [form, setForm] = useState(EMPTY_FORM);

  const liveMetrics = useMemo(() => {
    const pick = (m) => (m && m.source !== 'none' && m.rawValue != null ? m.rawValue : null);
    const sm = salesData.metrics || {};
    const fm = financeData.metrics || {};
    const leadsActual = marketingData.leadsActual || null;
    return {
      revenue: pick(sm.revenue),
      projectsSold: pick(sm.projectsSold),
      avgJobSize: pick(sm.avgJobSize),
      closeRatio: pick(sm.closeRatio),
      leads_month: leadsActual?.month ?? null,
      leads_quarter: leadsActual?.quarter ?? null,
      leads_year: leadsActual?.annual ?? pick(marketingData.metrics?.leads),
      gpm: pick(fm.gpm),
      grossProfit: pick(fm.grossProfit),
      netMargin: pick(fm.netMargin),
      netProfit: pick(fm.netProfit)
    };
  }, [salesData.metrics, marketingData.metrics, marketingData.leadsActual, financeData.metrics]);

  // The value a goal card should show as "current" — live when linked and available.
  const currentValueFor = (goal) => {
    if (goal.linkedMetric && liveMetrics[goal.linkedMetric] != null) {
      return roundForUnit(liveMetrics[goal.linkedMetric], goal.unit);
    }
    return Number(goal.actual) || 0;
  };

  // Tab from navigation source
  useEffect(() => {
    const fromParam = new URLSearchParams(location.search).get('from');
    const match = DEPARTMENTS.find((d) => d.toLowerCase() === (fromParam || '').toLowerCase());
    if (match) {
      setActiveTab(match);
    } else if (isOnboardingMode) {
      setActiveTab('Sales');
    }
  }, [isOnboardingMode, location.search]);

  // Load goals
  useEffect(() => {
    if (!goalsOwnerId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const userGoals = await getUserGoals(goalsOwnerId);
        if (!cancelled) setGoals(userGoals || {});
      } catch (error) {
        console.error('Error loading user goals:', error);
        if (!cancelled) setGoals({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [goalsOwnerId]);

  // Onboarding: mark sales goals step complete
  useEffect(() => {
    if (isOnboardingMode && activeTab === 'Sales') {
      const timer = setTimeout(() => markStepComplete('salesGoals'), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnboardingMode, activeTab, markStepComplete]);

  // Silently persist live JobTread values into linked goals so other surfaces
  // (dashboard, department goal strips) read fresh numbers. Runs once per load.
  const syncedRef = useRef(false);
  useEffect(() => {
    if (syncedRef.current || loading || liveLoading || !goalsOwnerId) return;
    const updates = [];
    Object.entries(goals).forEach(([dept, deptGoals]) => {
      (deptGoals || []).forEach((goal) => {
        if (!goal.linkedMetric || liveMetrics[goal.linkedMetric] == null) return;
        const liveValue = roundForUnit(liveMetrics[goal.linkedMetric], goal.unit);
        if (liveValue !== goal.actual) updates.push({ dept, goalId: goal.id, actual: liveValue });
      });
    });
    if (updates.length === 0) {
      syncedRef.current = true;
      return;
    }
    syncedRef.current = true;
    (async () => {
      // Sequential: updateUserGoal rewrites the whole doc per call.
      for (const u of updates) {
        try {
          await updateUserGoal(goalsOwnerId, u.dept, u.goalId, { actual: u.actual });
        } catch (error) {
          console.error('Error syncing linked goal value:', error);
        }
      }
      setGoals((prev) => {
        const next = { ...prev };
        updates.forEach((u) => {
          next[u.dept] = (next[u.dept] || []).map((g) =>
            g.id === u.goalId ? { ...g, actual: u.actual } : g
          );
        });
        return next;
      });
    })();
  }, [loading, liveLoading, goalsOwnerId, goals, liveMetrics]);

  // Onboarding inline current-value editing (Sales tab)
  const handleCurrentValueChange = async (goalId, newValue) => {
    if (!goalsOwnerId) return;
    const newActual = parseFloat(newValue) || 0;
    setGoals((prev) => ({
      ...prev,
      [activeTab]: (prev[activeTab] || []).map((goal) =>
        goal.id === goalId ? { ...goal, actual: newActual } : goal
      )
    }));
    try {
      await updateUserGoal(goalsOwnerId, activeTab, goalId, { actual: newActual });
    } catch (error) {
      console.error('Error updating goal current value:', error);
    }
  };

  // ----- Modal handlers -----
  const openAddModal = () => {
    setEditingGoal(null);
    setEditingDept(null);
    setForm({ ...EMPTY_FORM, department: activeTab });
    setModalOpen(true);
  };

  const openEditModal = (goal) => {
    setEditingGoal(goal);
    setEditingDept(activeTab);
    setForm({
      metricName: goal.metricName || '',
      department: activeTab,
      unit: goal.unit || 'usd',
      target: goal.target ?? '',
      actual: goal.actual ?? '',
      linkedMetric: goal.linkedMetric || ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingGoal(null);
    setEditingDept(null);
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleLinkedMetricChange = (key) => {
    setForm((prev) => ({
      ...prev,
      linkedMetric: key,
      unit: key ? LINKED_METRICS[key].unit : prev.unit,
      metricName:
        key && !prev.metricName.trim()
          ? `${LINKED_METRICS[key].label} (${LINKED_METRICS[key].window})`
          : prev.metricName
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.metricName.trim() || !goalsOwnerId || saving) return;

    const linked = form.linkedMetric || null;
    const liveValue = linked && liveMetrics[linked] != null ? liveMetrics[linked] : null;
    const unit = linked ? LINKED_METRICS[linked].unit : form.unit;
    const payload = {
      metricName: form.metricName.trim(),
      description: '',
      unit,
      target: parseFloat(form.target) || 0,
      actual: linked
        ? roundForUnit(liveValue ?? (parseFloat(form.actual) || 0), unit)
        : parseFloat(form.actual) || 0,
      linkedMetric: linked,
      department: form.department
    };

    setSaving(true);
    try {
      if (editingGoal) {
        if (form.department !== editingDept) {
          // Moved departments: remove from the old list, add to the new one.
          const moved = { ...editingGoal, ...payload };
          await deleteUserGoal(goalsOwnerId, editingDept, editingGoal.id);
          await addUserGoal(goalsOwnerId, form.department, moved);
          setGoals((prev) => ({
            ...prev,
            [editingDept]: (prev[editingDept] || []).filter((g) => g.id !== editingGoal.id),
            [form.department]: [...(prev[form.department] || []), moved]
          }));
        } else {
          await updateUserGoal(goalsOwnerId, form.department, editingGoal.id, payload);
          setGoals((prev) => ({
            ...prev,
            [form.department]: (prev[form.department] || []).map((g) =>
              g.id === editingGoal.id ? { ...g, ...payload } : g
            )
          }));
        }
      } else {
        const newGoal = { id: Date.now(), ...payload };
        await addUserGoal(goalsOwnerId, form.department, newGoal);
        setGoals((prev) => ({
          ...prev,
          [form.department]: [...(prev[form.department] || []), newGoal]
        }));
      }
      closeModal();
    } catch (error) {
      console.error('Error saving goal:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingGoal || !goalsOwnerId || saving) return;
    if (!window.confirm('Delete this goal? This action cannot be undone.')) return;
    setSaving(true);
    try {
      await deleteUserGoal(goalsOwnerId, editingDept, editingGoal.id);
      setGoals((prev) => ({
        ...prev,
        [editingDept]: (prev[editingDept] || []).filter((g) => g.id !== editingGoal.id)
      }));
      closeModal();
    } catch (error) {
      console.error('Error deleting goal:', error);
    } finally {
      setSaving(false);
    }
  };

  const currentGoals = goals[activeTab] || [];
  const formLinkedInfo = form.linkedMetric ? LINKED_METRICS[form.linkedMetric] : null;
  const formLiveValue = form.linkedMetric ? liveMetrics[form.linkedMetric] : null;

  if (loading) {
    return (
      <div className="relative p-6 min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading your goals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen p-6">
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto">
      {/* Header */}
      <div className="relative flex items-center justify-center mb-2">
        <div className="absolute left-0">
          <BackButton onClick={() => window.history.back()} />
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white text-center">Goals</h1>
        <div className="absolute right-0">
          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Goal
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
        Track your company targets across every department
      </p>

      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {DEPARTMENTS.map((tab) => {
          const style = DEPARTMENT_STYLES[tab];
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                isActive ? style.active : style.light
              }`}
            >
              <span aria-hidden="true">{style.emoji}</span>
              {tab}
            </button>
          );
        })}
      </div>

      {/* Onboarding banner */}
      {isOnboardingMode && activeTab === 'Sales' && (
        <div className="mb-6 p-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <div className="flex items-center justify-center gap-2 text-blue-700 dark:text-blue-300">
            <Target className="w-4 h-4 shrink-0" />
            <span className="font-medium text-sm">
              Enter your current values for each goal. Targets and status are pre-set.
            </span>
          </div>
        </div>
      )}

      {/* Goal cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {currentGoals.map((goal) => {
          const current = currentValueFor(goal);
          const progress = calculateProgress(current, goal.target);
          const tone = progressTone(progress);
          const isOnboardingSales = isOnboardingMode && activeTab === 'Sales';
          const isLinked = !!goal.linkedMetric;
          const linkedInfo = isLinked ? LINKED_METRICS[goal.linkedMetric] : null;

          return (
            <div
              key={goal.id}
              onClick={() => openEditModal(goal)}
              className="group relative rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer p-5"
            >
              {/* Top-right: sync badge + edit affordance */}
              <div className="absolute top-4 right-4 flex items-center gap-1.5">
                {isLinked && <SyncedBadge />}
                <Pencil className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors" />
              </div>

              {/* Centered header */}
              <div className="flex flex-col items-center text-center mb-5">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{goal.metricName}</h3>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                    Current
                  </p>
                  {isOnboardingSales && !isLinked ? (
                    <input
                      type="number"
                      value={goal.actual}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleCurrentValueChange(goal.id, e.target.value)}
                      className="w-full text-lg font-bold text-center bg-transparent border-b-2 border-red-500 focus:outline-none focus:border-red-600 text-red-600 dark:text-red-400"
                      placeholder="0"
                      min="0"
                      step={goal.unit === 'percent' ? '0.1' : '1'}
                    />
                  ) : (
                    <p className={`text-lg font-bold leading-tight ${tone.text}`}>
                      {isLinked && liveLoading && liveMetrics[goal.linkedMetric] == null ? (
                        <span className="text-gray-300 dark:text-gray-600">…</span>
                      ) : (
                        formatValue(current, goal.unit)
                      )}
                    </p>
                  )}
                  {isLinked && (
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{linkedInfo?.window}</p>
                  )}
                </div>
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                    Target
                  </p>
                  <p className="text-lg font-bold leading-tight text-gray-900 dark:text-white">
                    {formatValue(goal.target, goal.unit)}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-3 text-center">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1">
                    Reached
                  </p>
                  <p className={`text-lg font-bold leading-tight ${tone.text}`}>{progress}%</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${tone.bar}`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {currentGoals.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 py-16 text-center">
          <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
            <Target className="w-6 h-6" />
          </div>
          <p className="text-gray-700 dark:text-gray-200 font-medium mb-1">No {activeTab} goals yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Set a target and track your progress — or sync it straight from JobTread.
          </p>
          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Your First Goal
          </button>
        </div>
      )}

      {/* Onboarding continue */}
      {isOnboardingMode && (
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/start/existing-conditions')}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105"
          >
            Continue Onboarding
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add / Edit goal modal */}
      <Modal isOpen={modalOpen} onClose={closeModal} maxWidth="xl">
        <div className="max-h-[80vh] overflow-y-auto pr-1 -mr-1">
          <div className="flex flex-col items-center text-center mb-5">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {editingGoal ? 'Edit Goal' : 'New Goal'}
            </h2>
            {!editingGoal && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Set a target and choose how to track it
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {editingGoal ? (
              <div>
                <label className={labelClass}>Goal Name *</label>
                <input
                  type="text"
                  value={form.metricName}
                  onChange={(e) => setField('metricName', e.target.value)}
                  className={inputClass}
                  placeholder="e.g., Annual Revenue"
                  required
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelClass}>Goal Name *</label>
                  <input
                    type="text"
                    value={form.metricName}
                    onChange={(e) => setField('metricName', e.target.value)}
                    className={inputClass}
                    placeholder="e.g., Annual Revenue"
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Department</label>
                  <select
                    value={form.department}
                    onChange={(e) => setField('department', e.target.value)}
                    className={inputClass}
                  >
                    {DEPARTMENTS.map((dept) => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Target — the hero of the form */}
            <div>
              <label className={labelClass}>Target *</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center pointer-events-none">
                  <Target className="w-5 h-5" />
                </div>
                <input
                  type="number"
                  value={form.target}
                  onChange={(e) => setField('target', e.target.value)}
                  className="w-full pl-16 pr-24 py-4 rounded-xl border-2 border-red-200 dark:border-red-800/60 bg-white dark:bg-gray-800 text-2xl font-bold tracking-tight text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  required
                />
                {form.linkedMetric ? (
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400 dark:text-gray-500">
                    {UNIT_SHORT[LINKED_METRICS[form.linkedMetric].unit]}
                  </span>
                ) : (
                  <select
                    value={form.unit}
                    onChange={(e) => setField('unit', e.target.value)}
                    title="Unit"
                    className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                  >
                    {UNIT_OPTIONS.map((u) => (
                      <option key={u.value} value={u.value}>{UNIT_SHORT[u.value]}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Data source toggle */}
            <div>
              <label className={labelClass}>Current Value Tracking</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleLinkedMetricChange('')}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    !form.linkedMetric
                      ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Manual entry
                </button>
                <button
                  type="button"
                  onClick={() => handleLinkedMetricChange(form.linkedMetric || 'revenue')}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    form.linkedMetric
                      ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Zap className="w-3.5 h-3.5" />
                  Sync from JobTread
                </button>
              </div>
            </div>

            {form.linkedMetric ? (
              <>
                <div>
                  <label className={labelClass}>JobTread Metric</label>
                  <select
                    value={form.linkedMetric}
                    onChange={(e) => handleLinkedMetricChange(e.target.value)}
                    className={inputClass}
                  >
                    {METRIC_GROUPS.map((group) => (
                      <optgroup key={group} label={group}>
                        {Object.entries(LINKED_METRICS)
                          .filter(([, m]) => m.group === group)
                          .map(([key, m]) => (
                            <option key={key} value={key}>
                              {m.label} — {m.window.toLowerCase()}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="rounded-lg border border-orange-200 dark:border-orange-800/50 bg-orange-50 dark:bg-orange-900/20 px-3.5 py-2.5 text-sm text-orange-800 dark:text-orange-200 flex items-center gap-2">
                  <Zap className="w-4 h-4 shrink-0" />
                  {!jobtreadConnected ? (
                    <span>Connect JobTread to sync this value automatically.</span>
                  ) : liveLoading ? (
                    <span>Loading live value…</span>
                  ) : formLiveValue != null ? (
                    <span>
                      Current value syncs automatically:{' '}
                      <strong>{formatValue(roundForUnit(formLiveValue, formLinkedInfo.unit), formLinkedInfo.unit)}</strong>{' '}
                      ({formLinkedInfo.window.toLowerCase()})
                    </span>
                  ) : (
                    <span>No JobTread data for this metric yet — it will sync once data exists.</span>
                  )}
                </div>
              </>
            ) : (
              <div>
                <label className={labelClass}>Current</label>
                <input
                  type="number"
                  value={form.actual}
                  onChange={(e) => setField('actual', e.target.value)}
                  className={inputClass}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <div>
                {editingGoal && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingGoal ? 'Save Changes' : 'Add Goal'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default Goals;
